import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  SERVICE_TYPE_LABELS,
  ServiceType,
} from '../../../../happypaws-shared/src/Services/services';
import { SitterPricing } from '../../../../happypaws-shared/src/Sitter/models/SitterProfileResponse';

interface PricingRow {
  id: string;
  service: ServiceType;
  priceMin: number;
  priceMax: number;
}

interface ServicesPricingSectionProps {
  data: SitterPricing[];
  onSave?: (rows: PricingRow[]) => void;
}

const ALL_SERVICE_TYPES = Object.values(ServiceType);
const PRICE_MIN = 0;
const PRICE_MAX = 300;

const ServicesPricingSection: React.FC<ServicesPricingSectionProps> = ({ data, onSave }) => {
  const [rows, setRows] = useState<PricingRow[]>([]);

  useEffect(() => {
    if (data?.length) {
      setRows(
        data.map((d) => ({
          id: d.id,
          service: d.service,
          priceMin: d.priceMin,
          priceMax: d.priceMax,
        }))
      );
    }
  }, [data]);

  const usedServices = rows.map((r) => r.service);
  const availableServices = ALL_SERVICE_TYPES.filter((s) => !usedServices.includes(s));

  const handleAddService = () => {
    if (!availableServices.length) return;
    setRows((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        service: availableServices[0],
        priceMin: 10,
        priceMax: 50,
      },
    ]);
  };

  const handleRemove = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleServiceChange = (id: string, service: ServiceType) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, service } : r)));
  };

  const handleSliderChange = (id: string, value: number[]) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, priceMin: value[0], priceMax: value[1] } : r))
    );
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Services & Pricing
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure the services you offer and your rates
      </Typography>

      <Stack spacing={3}>
        {rows.map((row) => {
          const rowAvailable = ALL_SERVICE_TYPES.filter(
            (s) => s === row.service || !usedServices.filter((u) => u !== row.service).includes(s)
          );

          return (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: '#FAFAFA',
              }}
            >
              {/* Left: service selector + price labels */}
              <Box sx={{ flex: '0 0 220px' }}>
                <FormControl fullWidth size="small">
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 0.5, color: 'text.secondary' }}>
                    Service
                  </Typography>
                  <Select
                    value={row.service}
                    onChange={(e) => handleServiceChange(row.id, e.target.value as ServiceType)}
                    sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px' }}
                  >
                    {rowAvailable.map((s) => (
                      <MenuItem key={s} value={s}>
                        {SERVICE_TYPE_LABELS[s]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Min price</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#178B8E' }}>
                      ${row.priceMin}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Max price</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#178B8E' }}>
                      ${row.priceMax}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Right: range slider */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 1, color: 'text.secondary' }}>
                  Price range ($ / hour)
                </Typography>
                <Slider
                  value={[row.priceMin, row.priceMax]}
                  onChange={(_, value) => handleSliderChange(row.id, value as number[])}
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `$${v}`}
                  sx={{
                    color: '#178B8E',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#178B8E',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#178B8E',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">${PRICE_MIN}</Typography>
                  <Typography variant="caption" color="text.secondary">${PRICE_MAX}</Typography>
                </Box>
              </Box>

              {/* Remove button */}
              {rows.length > 1 && (
                <IconButton
                  onClick={() => handleRemove(row.id)}
                  size="small"
                  sx={{
                    color: '#B0B0B0',
                    '&:hover': { color: '#D32F2F', backgroundColor: '#FFEBEE' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Stack>

      {availableServices.length > 0 && (
        <Button
          onClick={handleAddService}
          sx={{
            mt: 2,
            color: '#178B8E',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
          }}
        >
          + Add service
        </Button>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onSave?.(rows)}
          sx={{
            textTransform: 'none',
            backgroundColor: '#178B8E',
            '&:hover': { backgroundColor: '#146f72' },
          }}
        >
          Save Changes
        </Button>
      </Box>
    </Paper>
  );
};

export default ServicesPricingSection;
